/**
 * Shop by Colour — Matching Sets controller.
 * Pre-rendered panels; swatch selection toggles visibility only.
 */
(function () {
  const ROOT_SELECTOR = '.shop-by-colour';
  const SWATCH_SELECTOR = '.shop-by-colour__swatch';
  const PANEL_SELECTOR = '.shop-by-colour__panel';
  const SELECTED_CLASS = 'is-selected';
  const COLOUR_PARAM = 'colour';
  const INIT_ATTR = 'data-shop-by-colour-init';

  const getColourFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get(COLOUR_PARAM) || '';
  };

  const updateUrlParam = (handle) => {
    if (!handle) return;

    const url = new URL(window.location.href);
    url.searchParams.set(COLOUR_PARAM, handle);
    window.history.replaceState({}, '', url.toString());
  };

  const init = (root) => {
    if (root.getAttribute(INIT_ATTR) === 'true') return;
    root.setAttribute(INIT_ATTR, 'true');

    const swatches = Array.from(root.querySelectorAll(SWATCH_SELECTOR));
    const panels = Array.from(root.querySelectorAll(PANEL_SELECTOR));
    if (swatches.length === 0 || panels.length === 0) return;

    const nameEls = Array.from(root.querySelectorAll('.js-colour-name'));
    const legendValueEl = root.querySelector('.js-colour-legend-value');
    const desktopPicker = root.querySelector('.shop-by-colour__picker-desktop');
    const previewEl = root.querySelector('.js-colour-swatch-preview');
    const liveRegion = root.querySelector('.js-colour-live-region');
    const searchInput = root.querySelector('.js-colour-search');
    const sheetTrigger = root.querySelector('.js-colour-sheet-trigger');
    const sheetClose = root.querySelector('.js-colour-sheet-close');
    const sheetBackdrop = root.querySelector('.js-colour-sheet-backdrop');
    const sheet = root.querySelector('.shop-by-colour__sheet');

    const panelsByHandle = new Map();
    for (const panel of panels) {
      const handle = panel.dataset.colourHandle;
      if (handle) panelsByHandle.set(handle, panel);
    }

    const swatchesByHandle = new Map();
    for (const swatch of swatches) {
      const handle = swatch.dataset.colourHandle;
      if (!handle) continue;
      if (!swatchesByHandle.has(handle)) swatchesByHandle.set(handle, []);
      swatchesByHandle.get(handle).push(swatch);
    }

    const firstHandle = root.dataset.firstHandle || swatches[0]?.dataset.colourHandle || '';
    let activeHandle = firstHandle;

    const getSelectedName = () => {
      const panel = panelsByHandle.get(activeHandle);
      if (panel?.dataset.colourName) return panel.dataset.colourName;

      const groupSwatches = swatchesByHandle.get(activeHandle);
      if (groupSwatches?.[0]?.dataset.colourName) return groupSwatches[0].dataset.colourName;

      return root.dataset.firstName || '';
    };

    const setLegendName = (name) => {
      if (!legendValueEl || !name) return;
      legendValueEl.textContent = name;
    };

    const resetLegendName = () => {
      setLegendName(getSelectedName());
    };

    const applySwatchStyle = (element, swatch) => {
      if (!element || !swatch) return;
      const style = swatch.dataset.swatchStyle || swatch.getAttribute('style') || '';
      if (style) element.setAttribute('style', style);
    };

    const announce = (name) => {
      if (!liveRegion || !name) return;
      const template = root.dataset.liveTemplate || '';
      liveRegion.textContent = template ? template.replace('[[colour]]', name) : name;
    };

    const filterSwatches = (query) => {
      const normalized = query.trim().toLowerCase();
      const mobileSwatches = root.querySelectorAll('.shop-by-colour__picker-sheet ' + SWATCH_SELECTOR);

      for (const swatch of mobileSwatches) {
        const name = (swatch.dataset.colourName || '').toLowerCase();
        const matches = normalized === '' || name.includes(normalized);
        swatch.toggleAttribute('hidden', !matches);
      }
    };

    const isSheetOpen = () => {
      if (!sheet) return false;
      return sheet.classList.contains('active') || sheetBackdrop?.classList.contains('is-active');
    };

    const closeSheet = (focusTrigger = true) => {
      if (!sheet || !isSheetOpen()) return;

      if (searchInput && sheet.contains(document.activeElement)) {
        searchInput.blur();
      }

      sheet.classList.remove('active');
      document.body.classList.remove('dock-open');
      sheet.setAttribute('aria-hidden', 'true');

      if (sheetBackdrop) {
        sheetBackdrop.classList.remove('is-active');
      }

      if (searchInput) {
        searchInput.value = '';
        filterSwatches('');
      }

      if (focusTrigger && sheetTrigger) {
        sheetTrigger.focus();
      }
    };

    const openSheet = () => {
      if (!sheet) return;

      if (window.dockManager) {
        window.dockManager.closeAll();
      }

      sheet.classList.add('active');
      document.body.classList.add('dock-open');
      sheet.removeAttribute('aria-hidden');

      if (sheetBackdrop) {
        sheetBackdrop.classList.add('is-active');
      }

      if (window.dockManager?.initSwipeListeners) {
        window.dockManager.initSwipeListeners(sheet);
      }

      const selected = root.querySelector(
        '.shop-by-colour__picker-sheet ' + SWATCH_SELECTOR + '.' + SELECTED_CLASS,
      );
      if (selected) {
        selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      requestAnimationFrame(() => {
        if (searchInput) searchInput.focus();
      });
    };

    const activate = (handle, options = {}) => {
      const { updateUrl = true, announceChange = true } = options;
      if (!handle || handle === activeHandle) return;

      const panel = panelsByHandle.get(handle);
      const groupSwatches = swatchesByHandle.get(handle);
      if (!panel || !groupSwatches?.length) return;

      activeHandle = handle;
      const name = panel.dataset.colourName || groupSwatches[0].dataset.colourName || '';

      for (const otherPanel of panels) {
        otherPanel.toggleAttribute('hidden', otherPanel !== panel);
      }

      for (const swatch of swatches) {
        const isActive = swatch.dataset.colourHandle === handle;
        swatch.classList.toggle(SELECTED_CLASS, isActive);
        swatch.setAttribute('aria-checked', isActive ? 'true' : 'false');
      }

      for (const nameEl of nameEls) {
        nameEl.textContent = name;
      }

      setLegendName(name);
      applySwatchStyle(previewEl, groupSwatches[0]);

      if (announceChange) announce(name);
      if (updateUrl) updateUrlParam(handle);
    };

    const syncFromUrl = () => {
      const urlHandle = getColourFromUrl();
      if (!urlHandle) return;

      if (!panelsByHandle.has(urlHandle)) {
        updateUrlParam(firstHandle);
        return;
      }

      activate(urlHandle, { updateUrl: false, announceChange: false });
    };

    syncFromUrl();

    for (const swatch of swatches) {
      swatch.addEventListener('click', () => {
        activate(swatch.dataset.colourHandle);
        if (isSheetOpen()) {
          closeSheet(false);
        }
      });
    }

    if (desktopPicker && legendValueEl) {
      const showLegendForSwatch = (swatch) => {
        if (!swatch?.matches(SWATCH_SELECTOR)) return;
        setLegendName(swatch.dataset.colourName || '');
      };

      const isMovingToAnotherSwatch = (relatedTarget) => {
        const nextSwatch = relatedTarget?.closest?.(SWATCH_SELECTOR);
        return Boolean(nextSwatch && desktopPicker.contains(nextSwatch));
      };

      desktopPicker.addEventListener('pointerover', (event) => {
        const swatch = event.target.closest(SWATCH_SELECTOR);
        if (swatch) showLegendForSwatch(swatch);
      });

      desktopPicker.addEventListener('pointerout', (event) => {
        const swatch = event.target.closest(SWATCH_SELECTOR);
        if (!swatch) return;
        if (isMovingToAnotherSwatch(event.relatedTarget)) return;
        resetLegendName();
      });

      desktopPicker.addEventListener('focusin', (event) => {
        const swatch = event.target.closest(SWATCH_SELECTOR);
        if (swatch) showLegendForSwatch(swatch);
      });

      desktopPicker.addEventListener('focusout', (event) => {
        if (desktopPicker.contains(event.relatedTarget)) return;
        resetLegendName();
      });
    }

    if (sheetTrigger) {
      sheetTrigger.addEventListener('click', (event) => {
        event.preventDefault();
        openSheet();
      });
    }

    if (sheetClose) {
      sheetClose.addEventListener('click', (event) => {
        event.preventDefault();
        closeSheet();
      });
    }

    if (sheetBackdrop) {
      sheetBackdrop.addEventListener('click', () => {
        closeSheet();
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        filterSwatches(searchInput.value);
      });
    }

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isSheetOpen()) {
        event.preventDefault();
        closeSheet();
        return;
      }

      if (!event.target.matches(SWATCH_SELECTOR)) return;

      const visibleSwatches = swatches.filter((swatch) => !swatch.hasAttribute('hidden'));
      const currentIndex = visibleSwatches.indexOf(event.target);
      if (currentIndex < 0) return;

      let nextIndex = -1;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % visibleSwatches.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + visibleSwatches.length) % visibleSwatches.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = visibleSwatches.length - 1;
      }

      if (nextIndex >= 0) {
        event.preventDefault();
        visibleSwatches[nextIndex].focus();
      }
    });
  };

  const syncSheetBackdrops = () => {
    for (const root of document.querySelectorAll(ROOT_SELECTOR)) {
      const sheet = root.querySelector('.shop-by-colour__sheet');
      const sheetBackdrop = root.querySelector('.js-colour-sheet-backdrop');
      if (!sheet || !sheetBackdrop) continue;

      const isOpen = sheet.classList.contains('active');
      sheetBackdrop.classList.toggle('is-active', isOpen);
      if (!isOpen) {
        sheet.setAttribute('aria-hidden', 'true');
      }
    }
  };

  document.addEventListener('keyup', (event) => {
    if (event.code === 'Escape') {
      requestAnimationFrame(syncSheetBackdrops);
    }
  });

  const initAll = () => {
    for (const root of document.querySelectorAll(ROOT_SELECTOR)) {
      init(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (root) {
      root.removeAttribute(INIT_ATTR);
      init(root);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (root) root.removeAttribute(INIT_ATTR);
  });
})();
